import { useEffect, useRef, useCallback, useState } from "react";

/**
 * useBroadcastSync — Cross-window state synchronization via BroadcastChannel.
 * 
 * The "master" window (LiveControlPage) broadcasts state updates.
 * "Slave" windows (DetachedPanelPage) receive updates and can send commands back.
 * 
 * IMPORTANT: Camera/WebRTC streams are NOT transported. Detached windows
 * show only serializable state (preview, program, sources, scenes, etc.)
 */

const CHANNEL_PREFIX = "glotech-regie-";

export function useBroadcastMaster(sessionId) {
  const channelRef = useRef(null);
  const [detachedWindows, setDetachedWindows] = useState(new Set());

  useEffect(() => {
    if (!sessionId) return;
    const ch = new BroadcastChannel(`${CHANNEL_PREFIX}${sessionId}`);
    channelRef.current = ch;

    ch.onmessage = (e) => {
      const msg = e.data;
      if (msg.type === "slave_connected") {
        setDetachedWindows(prev => new Set([...prev, msg.panelType]));
      }
      if (msg.type === "slave_disconnected") {
        setDetachedWindows(prev => {
          const next = new Set(prev);
          next.delete(msg.panelType);
          return next;
        });
      }
    };

    return () => { ch.close(); channelRef.current = null; };
  }, [sessionId]);

  const broadcastState = useCallback((state) => {
    if (channelRef.current) {
      channelRef.current.postMessage({ type: "state_update", payload: state });
    }
  }, []);

  const onCommand = useCallback((handler) => {
    if (!channelRef.current) return () => {};
    const listener = (e) => {
      if (e.data.type === "command") handler(e.data);
    };
    channelRef.current.addEventListener("message", listener);
    return () => channelRef.current?.removeEventListener("message", listener);
  }, []);

  return { broadcastState, onCommand, detachedWindows };
}

export function useBroadcastSlave(sessionId, panelType) {
  const channelRef = useRef(null);
  const [state, setState] = useState(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    const ch = new BroadcastChannel(`${CHANNEL_PREFIX}${sessionId}`);
    channelRef.current = ch;

    ch.postMessage({ type: "slave_connected", panelType });
    setConnected(true);

    ch.onmessage = (e) => {
      if (e.data.type === "state_update") {
        setState(e.data.payload);
      }
    };

    const handleUnload = () => {
      ch.postMessage({ type: "slave_disconnected", panelType });
    };
    window.addEventListener("beforeunload", handleUnload);

    return () => {
      handleUnload();
      window.removeEventListener("beforeunload", handleUnload);
      ch.close();
      channelRef.current = null;
    };
  }, [sessionId, panelType]);

  const sendCommand = useCallback((action, payload = {}) => {
    if (channelRef.current) {
      channelRef.current.postMessage({ type: "command", action, ...payload });
    }
  }, []);

  return { state, connected, sendCommand };
}
